import { useMutation, useQuery } from "@apollo/client";
import Warning from "@mui/icons-material/Warning";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { ExpandableSearchInput } from "components/ExpandableSearchInput";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { RecipientType } from "encamp-shared/src/constants/submissions";
import { getStateForSubmissionType } from "encamp-shared/src/submissions";
import { formatAddress } from "encamp-shared/src/utils/address";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  BulkCreateFacilitiesQuery,
  StaffUsersForAssignmentQuery,
  StaffUsersForAssignmentQueryVariables,
  SubmissionMethod,
  SubmissionType,
} from "generated-graphql/graphql";
import { useDebounce } from "hooks/useDebounce";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { BULK_CREATE_NEW_SUBMISSIONS_MUTATION } from "queries/submissions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { STAFF_USERS_QUERY } from "routes/Staff/WasteFulfillment/api";
import { toUtcMidnightIsoFromDateLike } from "util/dates";

// Query waste-enabled facilities for the current tenant, with state info
const BULK_CREATE_FACILITIES_QUERY = gql(`
  query BulkCreateFacilities($search: String, $page: Int, $pageSize: Int) {
    facilities: wasteEnabledFacilities(search: $search, page: $page, pageSize: $pageSize) {
      items {
        id
        name
        streetAddress1
        city
        state
        zip
        customerFacilityId
      }
      count
    }
  }
`);

// Query facilities that already have an active submission of a given type.
// Uses the facilities query with submission-aware filtering to find overlaps.
const ACTIVE_SUBMISSION_FACILITIES_QUERY = gql(`
  query ActiveSubmissionFacilities($search: String, $page: Int, $pageSize: Int) {
    facilities(search: $search, page: $page, pageSize: $pageSize) {
      items {
        id
      }
      count
    }
  }
`);

type FacilityRow = BulkCreateFacilitiesQuery["facilities"]["items"][number] & {
  hasActiveSubmission?: boolean;
};

type BulkCreateNewSubmissionsDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export function BulkCreateNewSubmissionsDialog({
  open,
  onClose,
  onSuccess,
}: BulkCreateNewSubmissionsDialogProps) {
  const { tenantId } = useTenant();
  const alerts = useAlerts();

  // Form state
  const [submissionType, setSubmissionType] = useState<SubmissionType | "">("");
  const [title, setTitle] = useState("");
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(null);
  const [submissionMethod, setSubmissionMethod] = useState<SubmissionMethod | "">("");
  const [recipient, setRecipient] = useState("");
  const [staffAssigneeSearch, setStaffAssigneeSearch] = useState("");
  const [selectedStaffAssignee, setSelectedStaffAssignee] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<Set<string>>(new Set());
  const [comment, setComment] = useState("");
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [facilitySearchText, setFacilitySearchText] = useState("");
  const [facilityPage, setFacilityPage] = useState(0);
  const facilityPageSize = 6;

  // Derived state code from selected submission type
  const stateFilter = useMemo(() => {
    if (!submissionType) return null;
    return getStateForSubmissionType(submissionType);
  }, [submissionType]);

  // Debounce the facility search text for backend queries
  const [debouncedFacilitySearch] = useDebounce(facilitySearchText, 300);

  // Build the search string for facilities query — filter by tenant, state, and free text
  const facilitiesSearch = useMemo(() => {
    let search = `tenantId:${tenantId}`;
    if (stateFilter) {
      search += ` state:${stateFilter}`;
    }
    const trimmed = debouncedFacilitySearch.trim();
    if (trimmed) {
      search += ` ${trimmed}`;
    }
    return search;
  }, [tenantId, stateFilter, debouncedFacilitySearch]);

  // Reset page when search or submission type changes
  useEffect(() => {
    setFacilityPage(0);
  }, [facilitiesSearch]);

  // Fetch facilities
  const { data: facilitiesData, loading: facilitiesLoading } = useQuery(
    BULK_CREATE_FACILITIES_QUERY,
    {
      variables: {
        search: facilitiesSearch,
        page: facilityPage,
        pageSize: facilityPageSize,
      },
      skip: !open || !submissionType,
    }
  );

  // Check for existing active submissions of the selected type.
  // Query FacilitySubmission -> Submission to find facilities that already have
  // an active (non-complete) submission of the same type in this tenant.
  const activeSubmissionFacilitiesSearch = useMemo(() => {
    if (!submissionType || !tenantId) return "";
    return `tenantId:${tenantId} hasActiveSubmissionOfType:${submissionType}`;
  }, [submissionType, tenantId]);

  const { data: activeSubmissionFacilitiesData } = useQuery(ACTIVE_SUBMISSION_FACILITIES_QUERY, {
    variables: {
      search: activeSubmissionFacilitiesSearch,
      page: 0,
      pageSize: 1000,
    },
    skip: !open || !submissionType,
    fetchPolicy: "network-only",
  });

  // Build a set of facility IDs that already have an active submission of this type
  const facilitiesWithActiveSubmissions = useMemo(() => {
    const ids = new Set<string>();
    if (activeSubmissionFacilitiesData?.facilities?.items) {
      for (const item of activeSubmissionFacilitiesData.facilities.items) {
        ids.add(item.id);
      }
    }
    return ids;
  }, [activeSubmissionFacilitiesData]);

  // Keep rowCount stable during loading to prevent MUI DataGrid from resetting the page
  const facilityRowCountRef = useRef(0);
  if (facilitiesData?.facilities?.count != null) {
    facilityRowCountRef.current = facilitiesData.facilities.count;
  }

  // Facility rows with active submission warning
  const facilityRows: FacilityRow[] = useMemo(() => {
    const items = facilitiesData?.facilities?.items ?? [];
    return items.map((f) => ({
      ...f,
      hasActiveSubmission: facilitiesWithActiveSubmissions.has(f.id),
    }));
  }, [facilitiesData, facilitiesWithActiveSubmissions]);

  // Staff users for assignee picker
  const { data: staffUsersData, loading: staffUsersLoading } = useQuery<
    StaffUsersForAssignmentQuery,
    StaffUsersForAssignmentQueryVariables
  >(STAFF_USERS_QUERY, {
    variables: { search: staffAssigneeSearch || null },
    skip: !open,
  });

  const staffUserOptions = useMemo(
    () =>
      (staffUsersData?.staffUsers?.items ?? []).map((u) => {
        const name = u.person ? `${u.person.first} ${u.person.last}` : u.email;
        return {
          id: u.id,
          label: name !== u.email ? `${name} (${u.email})` : u.email,
        };
      }),
    [staffUsersData]
  );

  // Mutation
  const [createSubmissions, { loading: creating }] = useMutation(
    BULK_CREATE_NEW_SUBMISSIONS_MUTATION,
    {
      refetchQueries: ["SubmissionVirtualEvents"],
      awaitRefetchQueries: true,
    }
  );

  // Auto-populate title when submission type changes
  useEffect(() => {
    if (submissionType) {
      setTitle(prettyPrintEnumValue(submissionType));
    }
  }, [submissionType]);

  // Clear facility selection when submission type changes
  useEffect(() => {
    setSelectedFacilityIds(new Set());
  }, [submissionType]);

  const hasChanges = useMemo(
    () =>
      !!submissionType ||
      !!title ||
      selectedDate != null ||
      !!submissionMethod ||
      !!recipient ||
      selectedStaffAssignee != null ||
      selectedFacilityIds.size > 0 ||
      !!comment,
    [
      submissionType,
      title,
      selectedDate,
      submissionMethod,
      recipient,
      selectedStaffAssignee,
      selectedFacilityIds,
      comment,
    ]
  );

  const performClose = useCallback(() => {
    setSubmissionType("");
    setTitle("");
    setSelectedDate(null);
    setSubmissionMethod("");
    setRecipient("");
    setSelectedStaffAssignee(null);
    setStaffAssigneeSearch("");
    setSelectedFacilityIds(new Set());
    setComment("");
    setShowCloseConfirmation(false);
    onClose();
  }, [onClose]);

  const handleClose = useCallback(() => {
    if (hasChanges) {
      setShowCloseConfirmation(true);
    } else {
      performClose();
    }
  }, [hasChanges, performClose]);

  const executeCreate = useCallback(
    async (facilityIds: string[]) => {
      if (!submissionType || !title || !selectedDate || !submissionMethod || !tenantId) return;

      try {
        const dueDateIso = selectedDate ? toUtcMidnightIsoFromDateLike(selectedDate) : undefined;

        const result = await createSubmissions({
          variables: {
            input: {
              tenantId,
              submissionType,
              title,
              facilityIds,
              dueDate: dueDateIso ?? undefined,
              submissionMethod: submissionMethod || undefined,
              recipient: recipient || undefined,
              staffAssigneeId: selectedStaffAssignee?.id ?? undefined,
              comment: comment || undefined,
            },
          },
        });

        const data = result.data?.bulkCreateNewSubmissions;
        if (data?.succeeded) {
          const created = data.updated ?? 0;
          alerts.success(
            <>
              Created {created} submission(s).
              <br />
              You can now add other details (reviewers, signers, etc.) from the submissions list.
            </>
          );
          performClose();
          onSuccess();
        } else {
          const created = data?.updated ?? 0;
          const failed = data?.failed ?? 0;
          const failedIds = new Set(data?.failedFacilityIds ?? []);
          const failedNames = facilityRows
            .filter((f) => failedIds.has(f.id))
            .map((f) => f.name ?? f.id);
          if (created > 0) {
            alerts.warning(
              <>
                Created {created} submission(s), but {failed} failed.
                {failedNames.length > 0 && (
                  <>
                    <br />
                    Failed facilities: {failedNames.join(", ")}
                  </>
                )}
              </>
            );
            performClose();
            onSuccess();
          } else {
            alerts.error(
              <>
                Failed to create submissions.
                {failedNames.length > 0 && (
                  <>
                    <br />
                    Failed facilities: {failedNames.join(", ")}
                  </>
                )}
              </>
            );
          }
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        alerts.error(`Failed to create submissions: ${message}`);
      }
    },
    [
      submissionType,
      title,
      selectedDate,
      tenantId,
      submissionMethod,
      recipient,
      selectedStaffAssignee,
      comment,
      createSubmissions,
      alerts,
      performClose,
      onSuccess,
      facilityRows,
    ]
  );

  const handleSubmit = async () => {
    if (
      !submissionType ||
      !title ||
      !selectedDate ||
      !submissionMethod ||
      selectedFacilityIds.size === 0 ||
      !tenantId
    ) {
      alerts.error("Please fill in all required fields and select at least one facility");
      return;
    }

    await executeCreate(Array.from(selectedFacilityIds));
  };

  const columns: CustomGridColDef<FacilityRow>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Facility",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "address",
        headerName: "Address",
        flex: 1,
        minWidth: 180,
        valueGetter: (_value, row) =>
          formatAddress(row.streetAddress1, row.city, row.state, row.zip),
      },
      {
        field: "customerFacilityId",
        headerName: "Facility ID",
        width: 140,
      },
      {
        field: "hasActiveSubmission",
        headerName: "",
        width: 50,
        sortable: false,
        renderCell: ({ row }) =>
          row.hasActiveSubmission ? (
            <Tooltip title="This facility already has an active submission of this type">
              <Warning color="warning" fontSize="small" />
            </Tooltip>
          ) : null,
      },
    ],
    []
  );

  // Check if any selected facilities have active submissions
  const selectedWithActiveSubmissions = useMemo(() => {
    const ids: string[] = [];
    for (const id of selectedFacilityIds) {
      if (facilitiesWithActiveSubmissions.has(id)) {
        ids.push(id);
      }
    }
    return ids;
  }, [selectedFacilityIds, facilitiesWithActiveSubmissions]);

  const selectedCount = selectedFacilityIds.size;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: 900,
            maxWidth: "90vw",
            maxHeight: "90vh",
          },
        },
      }}
      title="Bulk Create Submissions"
      description="Create one submission per selected facility, all sharing the same type and settings."
      primaryActionButtonProps={{
        onClick: handleSubmit,
        loading: creating,
        loadingText: "Creating...",
        hideStartIcon: true,
        saveText: `Create ${selectedCount} Submission${selectedCount !== 1 ? "s" : ""}`,
        disabled:
          creating ||
          !submissionType ||
          !title ||
          !selectedDate ||
          !submissionMethod ||
          !tenantId ||
          selectedFacilityIds.size === 0,
      }}
      cancelButtonProps={{ disabled: creating }}
    >
      {/* Submission Type */}
      <FormControl size="small" sx={{ width: "50%" }}>
        <InputLabel>Submission Type *</InputLabel>
        <Select
          value={submissionType}
          label="Submission Type *"
          onChange={(e) => setSubmissionType(e.target.value as SubmissionType)}
        >
          {Object.values(SubmissionType).map((value) => (
            <MenuItem key={value} value={value}>
              {prettyPrintEnumValue(value)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Facility pick list — always visible */}
      <Box
        sx={{ opacity: submissionType ? 1 : 0.5, pointerEvents: submissionType ? "auto" : "none" }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle2">
            Select Facilities *
            {stateFilter && (
              <Tooltip
                title={`Listing ${STATE_NAMES[stateFilter as keyof typeof STATE_NAMES] ?? stateFilter} facilities`}
              >
                <Chip label={stateFilter} size="small" sx={{ ml: 1 }} />
              </Tooltip>
            )}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: 32 }}>
            <Typography variant="body2" color="text.secondary">
              {selectedCount} selected
            </Typography>
            <ExpandableSearchInput
              value={facilitySearchText}
              onChange={setFacilitySearchText}
              placeholder="Search Facilities"
            />
          </Box>
        </Box>
        <DataGrid
          rows={facilityRows}
          columns={columns}
          loading={facilitiesLoading}
          checkboxSelection
          keepNonExistentRowsSelected
          rowSelectionModel={{ type: "include", ids: selectedFacilityIds }}
          onRowSelectionModelChange={(newSelection) =>
            setSelectedFacilityIds(new Set(Array.from(newSelection.ids).map(String)))
          }
          pagination
          paginationMode="server"
          rowCount={facilityRowCountRef.current}
          paginationModel={{ page: facilityPage, pageSize: facilityPageSize }}
          onPaginationModelChange={(model) => setFacilityPage(model.page)}
          pageSizeOptions={[facilityPageSize]}
          initialState={{
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
          sx={{ minHeight: 370, maxHeight: 400 }}
          noRowsMessage={
            submissionType ? "No facilities found" : "Select a submission type to see facilities"
          }
        />
        {selectedWithActiveSubmissions.length > 0 && (
          <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={500}>
              {selectedCount === 1
                ? "The selected facility already has an active submission of this type"
                : `${selectedWithActiveSubmissions.length} of the selected facilities already ${
                    selectedWithActiveSubmissions.length === 1 ? "has" : "have"
                  } an active submission of this type`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              You may want to double check that you are not creating a redundant submission for{" "}
              {selectedCount === 1 || selectedWithActiveSubmissions.length === 1
                ? "this facility"
                : "these facilities"}
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Title */}
      <TextField
        fullWidth
        size="small"
        label="Title *"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <DatePicker
          label="Due Date *"
          value={selectedDate}
          onChange={(newValue) => setSelectedDate(newValue)}
          slotProps={{
            textField: {
              size: "small",
              sx: { width: 200 },
            },
          }}
        />
        <FormControl size="small" sx={{ flex: 1, minWidth: 180 }}>
          <InputLabel>Submission Method *</InputLabel>
          <Select
            value={submissionMethod}
            label="Submission Method *"
            onChange={(e) => setSubmissionMethod(e.target.value as SubmissionMethod)}
          >
            {Object.values(SubmissionMethod).map((value) => (
              <MenuItem key={value} value={value}>
                {prettyPrintEnumValue(value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ flex: 1, minWidth: 180 }}>
          <InputLabel>Recipient</InputLabel>
          <Select
            value={recipient}
            label="Recipient"
            onChange={(e) => setRecipient(e.target.value)}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            <MenuItem value={RecipientType.REGULATORY_AGENCY}>Regulatory Agency</MenuItem>
            <MenuItem value={RecipientType.THIRD_PARTY}>Third Party</MenuItem>
            <MenuItem value={RecipientType.INTERNAL}>Internal</MenuItem>
            <MenuItem value={RecipientType.OTHER}>Other</MenuItem>
          </Select>
        </FormControl>
        <Autocomplete
          options={staffUserOptions}
          value={selectedStaffAssignee}
          onChange={(_event, newValue) => setSelectedStaffAssignee(newValue)}
          onInputChange={(_event, newInputValue) => setStaffAssigneeSearch(newInputValue)}
          loading={staffUsersLoading}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => <TextField {...params} label="Staff Assignee" size="small" />}
          sx={{ flex: 1, minWidth: 180 }}
        />
      </Box>

      {/* Comment */}
      <TextField
        fullWidth
        size="small"
        label="Comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        multiline
        minRows={2}
        maxRows={4}
        helperText="Add an internal note for staff. This will be posted on each submission individually."
      />

      {/* Close confirmation dialog */}
      {showCloseConfirmation && (
        <Dialog
          open
          onClose={() => setShowCloseConfirmation(false)}
          title="Discard changes?"
          description="You have unsaved changes. Are you sure you want to close?"
          primaryActionButtonProps={{
            onClick: performClose,
            saveText: "Discard",
            hideStartIcon: true,
          }}
          cancelButtonProps={{
            onClick: () => setShowCloseConfirmation(false),
          }}
        />
      )}
    </Dialog>
  );
}
